import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Eye, Trash2, UserX } from "lucide-react";

const STORAGE_KEY = "bulk_sms_excluded_contacts";

const ExclusionsViewDrawer = ({ isOpen, onClose, onExclusionsUpdate }) => {
  const [exclusions, setExclusions] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          setExclusions(JSON.parse(stored));
        } catch {
          setExclusions([]);
        }
      } else {
        setExclusions([]);
      }
    }
  }, [isOpen]);

  const removeExclusion = (phone_number) => {
    const updated = exclusions.filter((e) => e.phone_number !== phone_number);
    setExclusions(updated);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    onExclusionsUpdate(updated);
  };

  const clearAll = () => {
    setExclusions([]);
    sessionStorage.removeItem(STORAGE_KEY);
    onExclusionsUpdate([]);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 z-50 flex flex-col h-full bg-white shadow-2xl w-full max-w-md"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg border border-red-100">
                  <Eye className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-800">
                    Excluded Contacts
                    <span className="ml-2 text-sm font-normal text-red-600">
                      ({exclusions.length})
                    </span>
                  </h2>
                  <p className="text-xs text-gray-500">These contacts will be skipped in your campaign</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {exclusions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
                  <UserX className="w-10 h-10" />
                  <p className="text-sm">No exclusions selected</p>
                  <p className="text-xs text-gray-400">Use "Exclude Numbers" to select contacts</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {exclusions.map((contact) => {
                    const fullName =
                      [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—";
                    return (
                      <li
                        key={contact.phone_number}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="p-1.5 bg-red-50 rounded-full border border-red-100 flex-shrink-0">
                          <UserX className="w-3.5 h-3.5 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{fullName}</p>
                          <p className="text-xs text-gray-500">{contact.phone_number}</p>
                        </div>
                        <button
                          onClick={() => removeExclusion(contact.phone_number)}
                          className="p-1.5 text-gray-400 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors flex-shrink-0"
                          title="Remove exclusion"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer */}
            {exclusions.length > 0 && (
              <div className="px-4 py-4 border-t border-gray-200 bg-white flex-shrink-0 space-y-2">
                <button
                  onClick={clearAll}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear All Exclusions
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ExclusionsViewDrawer;
